/* eslint-disable react-refresh/only-export-components -- LANGUAGES, STORAGE_KEY, useLang and LanguageProvider are all part of the same i18n system and co-exported intentionally */
import React, {
  createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, ReactNode,
} from 'react';
import EN_DICT from './i18n-en.json';
import { isRealTranslation } from './constants';
import {
  DEFAULT_LANG, isSupportedLang, isRtl, langFromPath, type Lang as LangCode,
} from './languages';

export type Dict = Record<string, string>;
type Lang = LangCode;
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// The list itself lives in a React-free leaf module so prerender.mjs and
// hreflang.ts can read it without importing the component tree, and so
// languages.ts <-> i18n.tsx cannot become an import cycle. Re-exported here
// because every existing call site imports these two names from i18n.
export { LANGUAGES, type Lang } from './languages';

// Exported so LangSync can read a stored preference without duplicating the
// key — two copies of a storage key is how a preference quietly stops loading.
export const STORAGE_KEY = 'won-lang';
/** Older key some builds may have written the language preference under. */
const LEGACY_STORAGE_KEY = 'npw_lang';

// ─── Fetch cache ─────────────────────────────────────────────────────────────
// Module-level: survives re-renders and re-mounts, not a page refresh (intentional).
// Seeded with English, which ships in the bundle so SSR and first paint have strings.
const dictCache = new Map<Lang, Dict>([['en', EN_DICT as Dict]]);

export async function loadLocale(lang: Lang): Promise<Dict> {
  const cached = dictCache.get(lang);
  if (cached) return cached;
  try {
    const res = await fetch(`/locales/${lang}.json`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // A missing file often does NOT 404: dev servers and SPA hosts commonly serve
    // index.html for unknown paths, which would otherwise fail deeper in as a
    // confusing JSON parse error. Reject anything that isn't actually JSON.
    const type = res.headers.get('content-type') || '';
    if (!type.includes('json')) throw new Error(`expected JSON, got "${type || 'unknown'}"`);
    const dict: unknown = await res.json();
    if (!dict || typeof dict !== 'object' || Array.isArray(dict)) {
      throw new Error('locale file is not an object');
    }
    dictCache.set(lang, dict as Dict);
    return dict as Dict;
  } catch (err) {
    console.warn(`[i18n] Failed to load locale "${lang}":`, err);
    // Let callers distinguish a failed locale request from a real English
    // dictionary. Treating English as if it were the requested locale made a
    // failed first request permanent and could hydrate localized HTML with the
    // wrong language.
    throw err;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────
interface LanguageContextValue {
  lang: LangCode;
  /** An explicit choice from the picker — persisted. */
  rememberLang: (l: LangCode) => void;
  /** Follow a language the URL already states — not persisted. */
  adoptLang: (l: LangCode) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** True when a translated key exists, so callers can fall back deliberately. */
  has: (key: string) => boolean;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * One-time move of a language preference stored under the older key.
 * Self-cleaning: the legacy entry is removed once carried over, so this is two
 * storage reads on the first visit after the update and one no-op read after.
 *
 * Deliberately does NOT overwrite an existing current-key value — a user who
 * has already picked a language since updating must keep that choice rather
 * than have a stale legacy value clobber it.
 */
function migrateLegacyLangKey(): void {
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy) return;
    if (!localStorage.getItem(STORAGE_KEY) && isSupportedLang(legacy)) {
      localStorage.setItem(STORAGE_KEY, legacy);
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch { /* storage blocked */ }
}

/**
 * Resolve the starting language.
 *
 * The URL path wins over everything. It is the addressable, shareable,
 * crawlable statement of which language this page is — a stored preference
 * that overrode it would mean /es/about could render in German, which is both
 * wrong for the reader and a contradiction of the page's own hreflang and
 * canonical tags.
 *
 * `?lang=` is still honoured as a fallback so links shared before the move to
 * path prefixes keep working. LangSync rewrites those to the prefixed URL.
 */
function getInitialLang(override?: LangCode): LangCode {
  if (override) return override;
  migrateLegacyLangKey();

  if (typeof window !== 'undefined') {
    const fromPath = langFromPath(window.location.pathname);
    if (fromPath) return fromPath;

    const legacyParam = new URLSearchParams(window.location.search).get('lang');
    if (isSupportedLang(legacyParam)) return legacyParam;
  }
  // Stored preferences are handled after hydration by LangSync, which also
  // moves the visitor to the matching language-prefixed URL. Reading them here
  // would make the first client render disagree with the English SSG markup on
  // a bare URL.
  return DEFAULT_LANG;
}

interface LanguageProviderProps {
  children: ReactNode;
  /** Language used by SSR and the matching first client render. */
  initialLang?: LangCode;
  /** Dictionary shared by SSR and hydration so their first render matches. */
  initialDict?: Dict;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, initialLang, initialDict }) => {
  const [lang, setLangState] = useState<LangCode>(() => getInitialLang(initialLang));
  // Seeded with the SSR dictionary when pre-rendering, English otherwise, so
  // the first paint is real text rather than raw keys.
  const [dict, setDict] = useState<Dict>(initialDict ?? (EN_DICT as Dict));
  // Track which locale owns `dict`. The URL language changes before an
  // uncached dictionary can arrive, so the previous locale must not be treated
  // as the translation for the new route during that short loading window.
  const [dictLang, setDictLang] = useState<LangCode>(
    initialDict && initialLang ? initialLang : DEFAULT_LANG,
  );
  const [failedLang, setFailedLang] = useState<LangCode | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lang === DEFAULT_LANG) {
      setDict(EN_DICT as Dict);
      setDictLang(DEFAULT_LANG);
      setFailedLang(null);
      setLoading(false);
      return;
    }
    if (failedLang === lang) {
      setLoading(false);
      return;
    }
    if (failedLang && failedLang !== lang) setFailedLang(null);
    // Already seeded by SSR — refetching would flash the same strings back in.
    if (dictLang === lang && dictCache.get(lang) === dict) {
      setFailedLang(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadLocale(lang).then(d => {
      if (cancelled) return;
      setDict(d);
      setDictLang(lang);
      setFailedLang(null);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      // This is a temporary fallback, not a translation cached for `lang`, so
      // returning to the locale later will retry the request.
      setDict(EN_DICT as Dict);
      setDictLang(DEFAULT_LANG);
      setFailedLang(lang);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [lang, dict, dictLang, failedLang]);

  useClientLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = lang;
    // Keep client-side language switches consistent with the prerendered
    // document, which already marks Arabic and Urdu pages as RTL.
    document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
  }, [lang]);

  /**
   * Record a language the URL already reflects.
   *
   * Used by LangSync after a route change. It must not write localStorage:
   * merely visiting a shared /fr/ link would otherwise overwrite the visitor's
   * own choice, so the next bare URL they open would be French.
   */
  const adoptLang = useCallback((l: LangCode) => setLangState(l), []);

  /** An explicit choice from the language picker — this one is remembered. */
  const rememberLang = useCallback((l: LangCode) => {
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* storage blocked */ }
  }, []);

  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    // Missing keys fall back to English before falling back to the key itself.
    // A scaffolded placeholder counts as missing: the key is present in the locale
    // file but holds the English string behind a marker, so without this check the
    // `??` chain would short-circuit and render "[NEEDS TRANSLATION] …" to the user.
    const activeDict = dictLang === lang ? dict : (EN_DICT as Dict);
    const own = activeDict[key];
    const translated = isRealTranslation(own) ? own : undefined;
    let str = translated ?? (EN_DICT as Dict)[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        // Replace every occurrence. A repeated variable in an accessible label
        // must not leave a raw {token} behind after translation.
        str = str.split(`{${k}}`).join(String(v));
      }
    }
    return str;
  }, [dict, dictLang, lang]);

  /**
   * Whether `key` has a real translation in the active dictionary.
   *
   * t() can never signal a miss — it falls back to English and then to the key
   * itself, so `t('x') ?? fallback` is dead code and a missing key would render
   * the literal string "x". Callers that need a genuine fallback use this.
   */
  const has = useCallback((key: string): boolean => {
    if (dictLang !== lang) return false;
    if (lang !== DEFAULT_LANG && dict === (EN_DICT as Dict)) return false;
    return isRealTranslation(dict[key]);
  }, [dict, dictLang, lang]);

  return (
    <LanguageContext.Provider value={{
      lang,
      rememberLang,
      adoptLang,
      t,
      has,
      loading: loading || (dictLang !== lang && failedLang !== lang),
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
};
